const { abs, exp, log, pow, sqrt } = Math

const EPS = 1e-8
const fpmin = 1e-30
const visibleProbability = [200, 150, 100, 50, 20, 10, 1]
const zScore = {
  200: 1.282,
  150: 1.44,
  100: 1.645,
  50: 1.96,
  20: 2.33,
  10: 2.576,
  1: 3.291
}

const constants = {
  visibleProbability,
  confidenceLevels: visibleProbability.map((_, i) => 1 - visibleProbability[i] / 1000),
  zScore
}

//normal-distribution (-\infty, z]
const normaldist = to => {
  const z = to / sqrt(2)
  const t = 1 / (1 + 0.3275911 * abs(z))
  const a1 = 0.254829592
  const a2 = -0.284496736
  const a3 = 1.421413741
  const a4 = -1.453152027
  const a5 = 1.061405429
  const erf = 1 - ((((a5 * t + a4) * t + a3) * t + a2) * t + a1) * t * exp(-z * z)
  const sign = z < 0 ? -1 : 1
  return (1 / 2) * (1 + sign * erf)
}

/*
two-sample z-test for difference in proportion;
'N-1' correction applied (Doi: 10.1002/sim.2832)
Small sample if n1<30 or n2<30 or chi-square expected values <1
conf is expressed in 1/1000s, so 50 means .05
*/
const pValueByZ = z => 2 * (1 - normaldist(abs(z)))

const gammaln = x => {
  let j = 0
  const cof = [
    76.18009172947146, -86.50532032941678, 24.01409824083091, -1.231739572450155, 0.1208650973866179e-2,
    -0.5395239384953e-5
  ]
  let ser = 1.000000000190015
  let xx
  let y
  let tmp = (y = xx = x) + 5.5
  tmp -= (xx + 0.5) * log(tmp)
  for (; j < 6; j++) ser += cof[j] / ++y
  return log((2.5066282746310007 * ser) / xx) - tmp
}

const betacf = (x, a, b) => {
  let m = 1
  const qab = a + b
  const qap = a + 1
  const qam = a - 1
  let c = 1
  let d = 1 - (qab * x) / qap
  let m2
  let aa
  let del
  let h

  // These q's will be used in factors that occur in the coefficients
  if (abs(d) < fpmin) d = fpmin
  d = 1 / d
  h = d

  for (; m <= 100; m++) {
    m2 = 2 * m
    aa = (m * (b - m) * x) / ((qam + m2) * (a + m2))
    // One step (the even one) of the recurrence
    d = 1 + aa * d
    if (abs(d) < fpmin) d = fpmin
    c = 1 + aa / c
    if (abs(c) < fpmin) c = fpmin
    d = 1 / d
    h *= d * c
    aa = (-(a + m) * (qab + m) * x) / ((a + m2) * (qap + m2))
    // Next step of the recurrence (the odd one)
    d = 1 + aa * d
    if (abs(d) < fpmin) d = fpmin
    c = 1 + aa / c
    if (abs(c) < fpmin) c = fpmin
    d = 1 / d
    del = d * c
    h *= del
    if (abs(del - 1) < 3e-7) break
  }

  return h
}

const ibeta = (x, a, b) => {
  // Returns the incomplete beta function I_x(a,b)
  // Factors in front of the continued fraction.
  const bt = 0 === x || 1 === x ? 0 : exp(gammaln(a + b) - gammaln(a) - gammaln(b) + a * log(x) + b * log(1 - x))
  if (0 > x || 1 < x) return false
  if (x < (a + 1) / (a + b + 2))
    // Use continued fraction directly.
    return (bt * betacf(x, a, b)) / a
  // else use continued fraction after making the symmetry transformation.
  return 1 - (bt * betacf(1 - x, b, a)) / b
}

const ibetainv = (p, a, b) => {
  // Returns the inverse of the incomplete beta function
  const a1 = a - 1
  const b1 = b - 1
  let j = 0
  let lna
  let lnb
  let pp
  let t
  let u
  let err
  let x
  let al
  let h
  let w
  if (p <= 0) return 0
  if (p >= 1) return 1
  if (a >= 1 && b >= 1) {
    pp = p < 0.5 ? p : 1 - p
    t = sqrt(-2 * log(pp))
    x = (2.30753 + t * 0.27061) / (1 + t * (0.99229 + t * 0.04481)) - t
    if (p < 0.5) x = -x
    al = (x * x - 3) / 6
    h = 2 / (1 / (2 * a - 1) + 1 / (2 * b - 1))
    w = (x * sqrt(al + h)) / h - (1 / (2 * b - 1) - 1 / (2 * a - 1)) * (al + 5 / 6 - 2 / (3 * h))
    x = a / (a + b * exp(2 * w))
  } else {
    lna = log(a / (a + b))
    lnb = log(b / (a + b))
    t = exp(a * lna) / a
    u = exp(b * lnb) / b
    w = t + u
    x = p < t / w ? pow(a * w * p, 1 / a) : 1 - pow(b * w * (1 - p), 1 / b)
  }
  const afac = -gammaln(a) - gammaln(b) + gammaln(a + b)
  for (; j < 10; j++) {
    if (0 === x || 1 === x) return x
    err = ibeta(x, a, b) - p
    t = exp(a1 * log(x) + b1 * log(1 - x) + afac)
    u = err / t
    x -= t = u / (1 - 0.5 * Math.min(1, u * (a1 / x - b1 / (1 - x))))
    if (x <= 0) x = 0.5 * (x + t)
    if (x >= 1) x = 0.5 * (x + t + 1)
    if (abs(t) < EPS * x && j > 0) break
  }
  return x
}
const qbeta = ibetainv

const Betinc = (X, A, B) => {
  let A0 = 0
  let B0 = 1
  let A1 = 1
  let B1 = 1
  let M9 = 0
  let A2 = 0
  let C9
  while (abs((A1 - A2) / A1) > 0.00001) {
    A2 = A1
    C9 = (-(A + M9) * (A + B + M9) * X) / (A + 2 * M9) / (A + 2 * M9 + 1)
    A0 = A1 + C9 * A0
    B0 = B1 + C9 * B0
    M9 = M9 + 1
    C9 = (M9 * (B - M9) * X) / (A + 2 * M9 - 1) / (A + 2 * M9)
    A1 = A0 + C9 * A1
    B1 = B0 + C9 * B1
    A0 = A0 / B1
    B0 = B0 / B1
    A1 = A1 / B1
    B1 = 1
  }
  return A1 / A
}

const tcdf = (X, df) => {
  if (0 >= df) {
    throw new Error('Degrees of freedom must be positive')
  }
  const A = df / 2
  const S = A + 0.5
  const Z = df / (df + X * X)
  const BT = exp(gammaln(S) - gammaln(0.5) - gammaln(A) + A * log(Z) + 0.5 * log(1 - Z))
  const betacdf = Z < (A + 1) / (S + 2) ? BT * Betinc(Z, A, 0.5) : 1 - BT * Betinc(1 - Z, 0.5, A)
  let res = X < 0 ? betacdf / 2 : 1 - betacdf / 2
  res = Math.round(res * 100000) / 100000
  return res
}

const sigTest = (x1, x2, n1, n2, conf = 50) => {
  const p1 = x1 / n1
  const p2 = x2 / n2
  const n = n1 + n2
  const chiSquareCellExpectedValue1_1 = ((x1 + x2) * n1) / n
  const chiSquareCellExpectedValue1_2 = ((x1 + x2) * n2) / n
  const chiSquareCellExpectedValue2_1 = ((n - x1 - x2) * n1) / n
  const chiSquareCellExpectedValue2_2 = ((n - x1 - x2) * n2) / n
  if (
    n < 1 ||
    n1 < 30 ||
    n2 < 30 ||
    chiSquareCellExpectedValue1_1 < 1 ||
    chiSquareCellExpectedValue1_2 < 1 ||
    chiSquareCellExpectedValue2_1 < 1 ||
    chiSquareCellExpectedValue2_2 < 1
  ) {
    // small sample case
    return false
  }
  const pooledP = (x1 + x2) / (n1 + n2)
  const se = sqrt(pooledP * (1 - pooledP) * (1 / n1 + 1 / n2))
  const z = (sqrt((n - 1) / n) * (p1 - p2)) / se
  const pValue = pValueByZ(z)
  return pValue < conf / 1000
}

// eslint-disable-next-line max-params
const sigTestMeanTwoSample = (mu1, mu2, sd1, sd2, n1, n2, confidenceLevel = 0.95) => {
  //small sample case:
  if (n1 < 30 || n2 < 30) {
    return false
  }
  const se = sqrt(pow(sd1, 2) / n1 + pow(sd2, 2) / n2)
  const df = Math.round(pow(se, 4) / (pow(pow(sd1, 2) / n1, 2) / (n1 - 1) + pow(pow(sd2, 2) / n2, 2) / (n2 - 1)))
  const t = (mu1 - mu2) / se

  const pValue = 2 * (1 - tcdf(abs(t), df))
  const pValLimit = 1 - (isNaN(Number(confidenceLevel)) ? 0.95 : Number(confidenceLevel))
  return pValue < pValLimit
}

/*
error bars (two-sided test)  (blue range):
qbeta=beta quantile function similar to R's qbeta. R
Only works with count data (so not scores, not distribute summary, not reorder summary, etc)
*/
const calcErrorForNode = (amount, total, conf = 50) => {
  const conf_level = (1000 - conf) / 1000
  const errLO = amount - total * qbeta((1 - conf_level) / 2, 0.5 + amount, 0.5 + total - amount)
  const errHI = total * qbeta(1 - (1 - conf_level) / 2, 0.5 + amount, 0.5 + total - amount) - amount
  return [Math.max(0, errLO) || 0, Math.max(0, errHI) || 0]
}

const findNeighbors = (sorted_array, included_indexes, target_idx, threshold) => {
  const target_point = sorted_array[target_idx]
  let neighbors = []
  // below search
  const target_included_idx = included_indexes.indexOf(target_idx)
  for (let _i = target_included_idx; _i >= 0; _i--) {
    const distance = sorted_array[included_indexes[_i]].x - target_point.x
    if (distance > threshold) {
      break
    } else {
      neighbors.push(included_indexes[_i])
    }
  }
  neighbors = neighbors.reverse()
  // above search
  for (let _i = target_included_idx + 1; _i < included_indexes.length; _i++) {
    if (sorted_array[included_indexes[_i]].x - target_point.x > threshold) {
      break
    } else {
      neighbors.push(included_indexes[_i])
    }
  }
  // list of neighbor indexes
  return neighbors
}

const findNeighborsXY = (sorted_x, included_indexes, target_point_index, threshold) => {
  const target_point = sorted_x[target_point_index]

  // find target point in sorted arrays
  const x_neighbors_idxs = findNeighbors(sorted_x, included_indexes, target_point_index, threshold)

  const real_neighbors = x_neighbors_idxs.filter(
    nb_idx =>
      Math.sqrt(Math.pow(sorted_x[nb_idx].x - target_point.x, 2) + Math.pow(sorted_x[nb_idx].y - target_point.y, 2)) <=
      threshold
  )

  return real_neighbors
}

const findGroup = (sorted_array, included_indexes, threshold) => {
  let group_idxs = [] // resulting array of indexes
  let neighbors = [included_indexes[0]] // intermediate array of indexes
  let new_neighbors = [included_indexes[0]]

  let remaining_indexes = included_indexes.slice()
  while (neighbors.length > 0 && remaining_indexes.length > 0) {
    new_neighbors = []
    neighbors.forEach(target_idx => {
      const last_found_neighbors = findNeighborsXY(sorted_array, remaining_indexes, target_idx, threshold)
      new_neighbors = new_neighbors.concat(last_found_neighbors)
      remaining_indexes = remaining_indexes.filter(el => !last_found_neighbors.includes(el))
    })

    group_idxs = group_idxs.concat(new_neighbors)
    neighbors = new_neighbors
  }
  return group_idxs
}

const calcPointsGroups = (points = [], threshold = 0) => {
  const sortedByX = points.sort((a, b) => a.x - b.x)

  let _indexes = sortedByX.map((_p, index) => index)
  const point_groups = []

  while (_indexes.length > 0) {
    const neighbors_group = findGroup(sortedByX, _indexes, threshold)
    point_groups.push(neighbors_group)
    _indexes = _indexes.filter(idx => !neighbors_group.includes(idx))
  }

  const groups = point_groups.map(group => group.map(index => sortedByX[index]))
  return groups
}

export default {
  calcErrorForNode,
  calcPointsGroups,
  constants,
  normaldist,
  qbeta,
  tcdf,
  gammaln,
  sigTest,
  sigTestMeanTwoSample
}
